import { action, makeObservable, observable } from 'mobx';
import { getRoot } from 'mobx-easy';
import { PrivilegeTypes, Task, TaskStatus } from '@bringg/types';
import { tasksReducerByStartStatus } from '@bringg-frontend/monitoring-shared';

import RootStore from 'bringg-web/stores/root-store';
import { routesMonitoringActionsProvider, trailerMatch, vehicleMatch } from '../utils/routes-monitoring-actions';
import RoutesMonitoringStore from './routes-monitoring-store';
import { routesMonitoringRootStore } from './routes-monitoring-root-store';
import { hasAccess } from 'bringg-web/utils/privileges';
import { VehicleRealtimeAsyncEvent } from 'bringg-web/features/runs-assignment/vehicle-assignment/vehicle-assigner-modal-const';
import { getRouteTasks } from '../utils/route-utils';

export enum RoutesActionsType {
	MOVE_TO_PLANNING = 'moveToPlanning',
	MOVE_TO_DISPATCH = 'releaseToDrivers',
	DRIVER_ACTION = 'driverAction',
	VEHICLE_ACTION = 'vehicleAction'
}

export type RoutesAction = MoveToDispatchAction | MoveToPlanningAction | AssignDriverAction | AssignVehicleAction;

export type MoveToDispatchAction = {
	action: RoutesActionsType.MOVE_TO_DISPATCH;
};

export type MoveToPlanningAction = {
	action: RoutesActionsType.MOVE_TO_PLANNING;
};

export type AssignDriverAction = {
	action: RoutesActionsType.DRIVER_ACTION;
	actionParams: {
		driverId: number;
		startedTaskIds: number[];
	};
};

export type AssignVehicleAction = {
	action: RoutesActionsType.VEHICLE_ACTION;
	actionParams: {
		vehicleId: number;
	};
};

class RouteMonitoringActionsStore {
	loadingRoutes = new Set<number>();
	monitoringStore: RoutesMonitoringStore;
	allowMovingTasksToPlanningPhase: boolean;
	canSendToDrivers: boolean;
	allowAssignDriverToTask: boolean;
	allowToEditVehicle: boolean;

	constructor() {
		makeObservable(this, {
			loadingRoutes: observable,
			setLoadingRoutes: action
		});

		this.monitoringStore = routesMonitoringRootStore.getStore().routesMonitoringStore;
		const { merchantConfigurationsStore, usersStore } = getRoot<RootStore>().data;
		const { configuration } = merchantConfigurationsStore;
		// eslint-disable-next-line etc/no-commented-out-code
		this.allowMovingTasksToPlanningPhase = configuration.planning_phase_exists && true; // configuration.allow_moving_tasks_to_planning_phase;
		this.canSendToDrivers = hasAccess(usersStore.currentUser, 'send_to_drivers');
		this.allowAssignDriverToTask = hasAccess(usersStore.currentUser, PrivilegeTypes.ASSIGN_DRIVER_TO_TASK);
		this.allowToEditVehicle =
			configuration.enable_vehicles && hasAccess(usersStore.currentUser, 'allow_assign_vehicle_to_run');
	}

	setLoadingRoutes = (routeId: number) => {
		this.loadingRoutes.add(routeId);
	};

	startAssignVehicleListeners = (routeId: number, event: VehicleRealtimeAsyncEvent) => {
		const route = getRoot<RootStore>().data.runStore.get(routeId);

		this.loadingRoutes.add(routeId);

		const routeTasks = getRouteTasks(routeId);
		const taskVehicleId = routeTasks[0]?.vehicle_id;
		const trailerId = taskVehicleId !== route.vehicle_id ? taskVehicleId : null;

		const routesToWatch = [];
		let tasksIdsToWatch = [];

		if (!vehicleMatch(event.vehicleId, route.vehicle_id) && trailerMatch(event.trailerId, trailerId)) {
			routesToWatch.push(routeId);

			if (!trailerId && !event.isPowerUnit) {
				tasksIdsToWatch = route.task_ids;
			}
		} else if (vehicleMatch(event.vehicleId, route.vehicle_id) && !trailerMatch(event.trailerId, trailerId)) {
			tasksIdsToWatch = route.task_ids;
		} else if (!vehicleMatch(event.vehicleId, route.vehicle_id) && !trailerMatch(event.trailerId, trailerId)) {
			routesToWatch.push(routeId);
			tasksIdsToWatch = route.task_ids;
		}

		const { updatePromise, rejectPromise } = routesMonitoringActionsProvider.realtimeActionUpdate(
			routesToWatch,
			tasksIdsToWatch,
			{
				action: RoutesActionsType.VEHICLE_ACTION,
				actionParams: {
					vehicleId: !routesToWatch.length ? trailerId : route.vehicle_id
				}
			}
		);

		const onActionSuccess = async () => {
			try {
				await updatePromise;
			} catch (e) {
				console.error('AssignVehicleListener failed', e);
			} finally {
				this.loadingRoutes.delete(routeId);
			}
		};

		const onActionFail = () => {
			this.loadingRoutes.delete(routeId);
			rejectPromise();
		};

		return { onActionSuccess, onActionFail };
	};

	startAssignDriverListeners = (routeId?: number, tasks?: Task[]) => {
		let driverId;
		if (routeId) {
			driverId = getRoot<RootStore>().data.runStore.get(routeId).user_id;
		}

		const assignedTasks = routeId ? getRouteTasks(routeId) : tasks;

		this.loadingRoutes.add(routeId);

		const { startedTaskIds, unStartedTasksIds } = tasksReducerByStartStatus([...assignedTasks]);

		const {
			updatePromise,
			rejectPromise,
			updateEventParams: updatingResolvedEvents
		} = routesMonitoringActionsProvider.realtimeActionUpdate(
			[routeId],
			unStartedTasksIds,
			{
				action: RoutesActionsType.DRIVER_ACTION,
				actionParams: { driverId, startedTaskIds }
			},
			true
		);

		const onActionSuccess = async (result: { task_ids: number[]; target_run_id?: number }) => {
			const updatedRoutes = new Set([routeId]);
			result.target_run_id && updatedRoutes.add(result.target_run_id);

			updatingResolvedEvents([...updatedRoutes], result.task_ids);

			try {
				await updatePromise;
				if (!routeId) {
					routesMonitoringRootStore.getStore().selectedTasksStore.clearSelectedTasks();
				}
			} catch (e) {
				console.error('AssignDriverListener failed', e);
			} finally {
				this.loadingRoutes.delete(routeId);
			}
		};

		const onActionFail = () => {
			rejectPromise();
			updatePromise.catch(() => {
				this.loadingRoutes.delete(routeId);
			});
		};

		return { onActionSuccess, onActionFail };
	};

	readyToExecuteAction = async (routeAction: RoutesActionsType, routeId: number) => {
		this.loadingRoutes.add(routeId);

		const tasksIds = getRouteTasks(routeId)
			.filter(task => task.status !== TaskStatus.Done && task.status !== TaskStatus.Cancelled)
			.map(task => task.id);

		const { updatePromise, rejectPromise } = routesMonitoringActionsProvider.realtimeActionUpdate(
			[routeId],
			tasksIds,
			{ action: routeAction } as MoveToPlanningAction | MoveToDispatchAction
		);

		const response = await routesMonitoringActionsProvider.readyToExecuteUpdate(routeAction, tasksIds).catch(e => {
			console.error(`failed to execute ${routeAction}`, e);
			return { success: false };
		});

		if (!response.success) rejectPromise();

		try {
			await updatePromise;

			return true;
		} catch (e) {
			console.error('readyToExecuteAction failed', e);

			return false;
		} finally {
			this.loadingRoutes.delete(routeId);
		}
	};
}

export default RouteMonitoringActionsStore;
